import { IconButton } from '@creditornot/cb-button';
import { ArrowLeft, ArrowRight } from '@creditornot/cb-icons';
import { cbSemanticCssVars, colors } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { SegmentedControl } from '@creditornot/cb-segmented-control';
import { pxToRem } from '@creditornot/cb-utils';
import React, { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import styled from 'styled-components';

import { CampaignsPerformancePerDayData } from 'apis/merchant-gateway/insights/campaigns-performance/split/types';
import { MOBILE_BREAKPOINT, useIsMobile } from 'common/hooks/useIsMobile';
import { EntityType } from 'common/types';
import VerticalBarChart from 'modules/insights-v2/components/charts/VerticalBarChart';
import { ChartData, LegendChartType, Legends } from 'modules/insights-v2/components/charts/types';

import { AnalyticsChartLegendSeries } from './AnalyticsChartLegendSeries';
import { ChartCategory } from './types';
import { useCampaignModuleContext } from '../../context';

type AnalyticsChartProps = {
  currency: Currency;
  category: ChartCategory;
  isLoading?: boolean;
  rawChartData: CampaignsPerformancePerDayData[];
  onGoBack?: () => void;
  onGoForward?: () => void;
  onChangeCategory: (category: ChartCategory) => void;
};

const ChartHeader = styled.div`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  display: flex;
  gap: ${pxToRem(8)};
  margin-block-end: ${pxToRem(24)};
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  padding: ${pxToRem(16)};

  @media (max-width: ${MOBILE_BREAKPOINT + 1}px) {
    flex-direction: column;
  }
`;

const ChartNavigationControls = styled.div`
  display: flex;
  gap: ${pxToRem(8)};
`;

const ChartHeaderRightSide = styled.div`
  align-items: center;
  display: flex;
  gap: ${pxToRem(24)};

  @media (max-width: ${MOBILE_BREAKPOINT + 1}px) {
    justify-content: space-between;
    width: 100%;
    margin-block-start: ${pxToRem(8)};
  }
`;

const ChartBody = styled.div`
  margin: ${pxToRem(16)} ${pxToRem(24)};

  min-height: 250px;
  align-content: center;
  text-align: center;

  @media (max-width: ${MOBILE_BREAKPOINT + 1}px) {
    margin: 0;
  }
`;

const ChartWrapper = styled.div`
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-radius: 8px;
`;

const chartCategoryToNameMap = {
  [ChartCategory.SALES]: 'insights.performance.total-sales',
  [ChartCategory.ORDERS]: 'insights.performance.total-orders',
};

const CHART_CATEGORY_LIST = Object.values(ChartCategory) as ChartCategory[];

const getLegend = (
  { formatMessage }: IntlShape,
  category: ChartCategory,
  entityType: Extract<EntityType, 'venue' | 'merchant'>,
): Legends<'NEW_CUSTOMERS' | 'RETURNING_CUSTOMERS'> | Legends<'sales'> | Legends<'orders'> => {
  if (entityType === 'venue') {
    return {
      NEW_CUSTOMERS: {
        color: colors.wolt88,
        value: formatMessage({ id: 'campaigns.analytics.new-customers' }),
        type: LegendChartType.BAR,
      },
      RETURNING_CUSTOMERS: {
        color: colors.wolt32,
        value: formatMessage({ id: 'campaigns.analytics-returning-customers' }),
        type: LegendChartType.BAR,
      },
    };
  }

  if (category === ChartCategory.SALES) {
    return {
      sales: {
        color: colors.wolt88,
        value: formatMessage({ id: 'campaigns.analytics-sales' }),
        type: LegendChartType.BAR,
      },
    };
  }

  return {
    orders: {
      color: colors.wolt88,
      value: formatMessage({ id: 'campaigns.analytics-orders' }),
      type: LegendChartType.BAR,
    },
  };
};

const getChartData = (
  legend: Legends<'NEW_CUSTOMERS' | 'RETURNING_CUSTOMERS'> | Legends<'sales'> | Legends<'orders'>,
  chartData: CampaignsPerformancePerDayData[],
  category: ChartCategory,
) => {
  const chartDataForSelectedCategory: ChartData<typeof legend> = chartData.map(({ label, total, split }) => {
    const splitNewCustomer = split?.find(({ label }) => label === 'FIRST_TIME')?.total;
    const splitReturning = split?.find(({ label }) => label === 'RETURNING')?.total;

    if (category === ChartCategory.SALES) {
      return {
        name: label,
        summaryValue: total.salesWithVat,
        sales: total.salesWithVat,
        NEW_CUSTOMERS: splitNewCustomer?.salesWithVat,
        RETURNING_CUSTOMERS: splitReturning?.salesWithVat,
      };
    } else {
      return {
        name: label,
        summaryValue: total.orders,
        orders: total.orders,
        NEW_CUSTOMERS: splitNewCustomer?.orders,
        RETURNING_CUSTOMERS: splitReturning?.orders,
      };
    }
  });
  return chartDataForSelectedCategory;
};

export const AnalyticsChart = ({
  currency,
  category,
  rawChartData,
  isLoading,
  onGoBack,
  onGoForward,
  onChangeCategory,
}: AnalyticsChartProps) => {
  const isMobile = useIsMobile();
  const intl = useIntl();
  const { entityType } = useCampaignModuleContext();

  const legend = useMemo(() => getLegend(intl, category, entityType), [intl, category, entityType]);
  const chartData = useMemo(() => getChartData(legend, rawChartData, category), [legend, rawChartData, category]);

  return (
    <ChartWrapper>
      <ChartHeader>
        <SegmentedControl
          fixedWidths={isMobile}
          style={{ width: isMobile ? '100%' : 'auto' }}
          value={category}
          options={CHART_CATEGORY_LIST.map(value => ({
            value,
            label: intl.formatMessage({ id: `campaigns.analytics-${value}` }),
          }))}
          onChange={value => {
            value !== category && onChangeCategory(value);
          }}
        />
        <ChartHeaderRightSide>
          <AnalyticsChartLegendSeries legends={legend} />
          <ChartNavigationControls>
            <IconButton
              disabled={!onGoBack}
              alignment="boundingBox"
              aria-label="Back"
              background="static"
              onClick={onGoBack}
              size="small"
              variant="blue"
            >
              <ArrowLeft />
            </IconButton>
            <IconButton
              disabled={!onGoForward}
              alignment="boundingBox"
              aria-label="Next"
              background="static"
              onClick={onGoForward}
              size="small"
              variant="blue"
            >
              <ArrowRight />
            </IconButton>
          </ChartNavigationControls>
        </ChartHeaderRightSide>
      </ChartHeader>
      <ChartBody>
        {isLoading ? (
          <Spinner size="large" />
        ) : (
          <VerticalBarChart
            data={chartData}
            isStacked
            currency={ChartCategory.SALES === category ? currency : undefined}
            summaryKey={chartCategoryToNameMap[category]}
            legends={legend}
          />
        )}
      </ChartBody>
    </ChartWrapper>
  );
};
